/**
 * 详情信息 IndexedDB 数据库
 * 用于存储详情信息
 */

const DB_NAME = 'RoleplayGF_Details'
const DB_VERSION = 1
const STORE_NAME = 'details'

interface DetailData {
  id: string
  data: any
  updatedAt: number
}

class DetailDB {
  private db: IDBDatabase | null = null
  private initPromise: Promise<IDBDatabase> | null = null

  /**
   * 初始化数据库
   */
  private async init(): Promise<IDBDatabase> {
    if (this.db) {
      return this.db
    }

    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        console.error('❌ DetailDB 打开失败:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        console.log('🔄 DetailDB 初始化中...')
        const db = (event.target as IDBOpenDBRequest).result

        // 创建数据表
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' })
        }
      }
    })

    return this.initPromise
  }

  /**
   * 获取数据库实例
   */
  private async getDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init()
    }
    return this.db!
  }

  /**
   * 序列化数据，确保可以被 IndexedDB 存储
   */
  private serializeData(data: any): any {
    try {
      // 通过 JSON 序列化和反序列化来清理数据
      // 这会移除函数、循环引用等不可序列化的内容
      return JSON.parse(JSON.stringify(data))
    } catch (error) {
      console.error('❌ 数据序列化失败:', error)
      throw new Error('数据包含不可序列化的内容')
    }
  }

  /**
   * 保存详情数据
   * @param id 数据ID
   * @param data 数据内容
   */
  async save(id: string, data: any): Promise<void> {
    id = String(id)
    // 验证 id 是否有效
    if (!id || typeof id !== 'string') {
      throw new Error('无效的 ID')
    }

    const db = await this.getDB()

    // 序列化数据，确保可以被存储
    const serializedData = this.serializeData(data)

    const detailData: DetailData = {
      id,
      data: serializedData,
      updatedAt: Date.now()
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.put(detailData)

      request.onsuccess = () => {
        console.log(`✅ 详情已保存: ${id}`)
        resolve()
      }

      request.onerror = () => {
        console.error(`❌ 保存详情失败:`, request.error)
        reject(request.error)
      }
    })
  }

  /**
   * 获取详情数据
   * @param id 数据ID
   */
  async get(id: string): Promise<any | null> {
    id = String(id)
    // 验证 id 是否有效
    if (!id || typeof id !== 'string') {
      console.warn('⚠️ 无效的 ID:', id)
      return null
    }

    const db = await this.getDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(id)

      request.onsuccess = () => {
        const result = request.result as DetailData | undefined
        resolve(result ? result.data : null)
      }

      request.onerror = () => {
        console.error(`❌ 获取详情失败:`, request.error)
        reject(request.error)
      }
    })
  }

  /**
   * 修改详情数据
   * @param id 数据ID
   * @param data 新数据内容
   */
  async update(id: string, data: any): Promise<void> {
    id = String(id)
    // 验证 id 是否有效
    if (!id || typeof id !== 'string') {
      throw new Error('无效的 ID')
    }

    const db = await this.getDB()

    // 序列化数据，确保可以被存储
    const serializedData = this.serializeData(data)

    const detailData: DetailData = {
      id,
      data: serializedData,
      updatedAt: Date.now()
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.put(detailData)

      request.onsuccess = () => {
        console.log(`✅ 详情已修改: ${id}`)
        resolve()
      }

      request.onerror = () => {
        console.error(`❌ 修改详情失败:`, request.error)
        reject(request.error)
      }
    })
  }
  /**
   * 删除详情数据
   * @param id 数据ID
   * */ 
  async delete(id: string): Promise<void> {
    id = String(id)
    // 验证 id 是否有效
    if (!id || typeof id !== 'string') {
      throw new Error('无效的 ID')
    }
    
    const db = await this.getDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(id)

      request.onsuccess = () => {
        console.log(`✅ 详情已删除: ${id}`)
        resolve()
      }

      request.onerror = () => {
        console.error(`❌ 删除详情失败:`, request.error)
        reject(request.error)
      }
    })
  }
  /**
   * 删除所有详情信息
  */
  async deleteAll(): Promise<void> {
    const db = await this.getDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()

      request.onsuccess = () => {
        console.log(`✅ 所有详情已删除`)
        resolve()
      }

      request.onerror = () => {
        console.error(`❌ 删除所有详情失败:`, request.error)
        reject(request.error)
      }
    })
  }
}

// 导出单例实例
export const detailDB = new DetailDB()
